import React, { useEffect } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import { getCustomerReports, exportCustomerReports } from '../actions';
import { setBranchId } from '../../../auth/actions';
import { Button, Dropdown, Input } from '../../../../common/ui';
import PropTypes from 'prop-types';
import CustomerReportsTable from './CustomerReportsTable';
import DateRangePicker from '../../../../common/components/DateRangePicker';
import { useSearchParams } from 'react-router-dom';

const CustomerReports = ({
  setBranchId,
  getCustomerReports,
  exportCustomerReports,
  auth: { branchList, branchId, companyId },
  customerReportsState: { isLoading, customerReports, pagination, error },
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get('page'), 10) || 1;
  const limit = parseInt(searchParams.get('limit'), 10) || 10;
  const order = searchParams.get('order') || 'desc';
  const phone = searchParams.get('phone') || '';

  const getThisMonthDates = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      fromDate: firstDay.toLocaleDateString('en-CA'),
      toDate: lastDay.toLocaleDateString('en-CA'),
    };
  };

  const defaultDates = getThisMonthDates();
  let fromDate = searchParams.get('fromDate') || defaultDates.fromDate;
  let toDate = searchParams.get('toDate') || defaultDates.toDate;
  if (!fromDate || !toDate) {
    const todayDate = new Date().toLocaleDateString('en-CA');
    fromDate = fromDate || todayDate;
    toDate = toDate || todayDate;
  }

  const branchOptions = (branchList || []).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const handleExport = () => {
    exportCustomerReports({
      fromDate,
      toDate,
      order,
    });
  };

  const handlePhoneChange = (value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value && value.trim()) {
        next.set('phone', value.trim());
      } else {
        next.delete('phone');
      }
      next.set('page', '1');
      return next;
    });
  };

  const handleDateRangeChange = (dateRange) => {
    if (dateRange?.startDate && dateRange?.endDate) {
      const from = dateRange.startDate.toLocaleDateString('en-CA');
      const to = dateRange.endDate.toLocaleDateString('en-CA');
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('fromDate', from);
        next.set('toDate', to);
        next.set('page', '1');
        return next;
      });
    } else {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('fromDate');
        next.delete('toDate');
        next.set('page', '1');
        return next;
      });
    }
  };

  useEffect(() => {
    if (companyId && branchId) {
      const apiParams = {
        order,
        limit,
        page,
        fromDate,
        toDate,
      };
      if (phone && phone.trim()) {
        apiParams.phone = phone.trim();
      }
      getCustomerReports(apiParams);
    }
  }, [
    companyId,
    branchId,
    page,
    limit,
    order,
    fromDate,
    toDate,
    phone,
    getCustomerReports,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>Customer Reports</h1>
          <p className={styles.breadcrumb}>Reports / Customers</p>
        </div>
        {branchId != null && branchOptions.length > 0 && (
          <div className={styles.branchWrap}>
            <Dropdown
              label="Branch"
              options={branchOptions}
              value={branchId}
              onChange={handleChangeBranch}
              placeholder="Select branch"
            />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarRight}>
              <div className={styles.searchWrap}>
                <Input
                  placeholder="Customer T.P"
                  value={phone}
                  onChange={handlePhoneChange}
                />
              </div>
              <div className={styles.dateRangeWrap}>
                <DateRangePicker
                  onDateRangeChange={handleDateRangeChange}
                  initialStartDate={new Date(fromDate)}
                  initialEndDate={new Date(toDate)}
                />
              </div>
              <Button variant="secondary" onClick={handleExport}>
                Export
              </Button>
            </div>
          </div>
          <div className={styles.tableWrap}>
            <CustomerReportsTable
              data={customerReports}
              isLoading={isLoading}
              error={error}
              pagination={pagination}
              setSearchParams={setSearchParams}
              page={page}
              limit={limit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

CustomerReports.propTypes = {
  setBranchId: PropTypes.func.isRequired,
  getCustomerReports: PropTypes.func.isRequired,
  exportCustomerReports: PropTypes.func.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  customerReportsState: PropTypes.shape({
    isLoading: PropTypes.bool,
    customerReports: PropTypes.array,
    pagination: PropTypes.object,
    error: PropTypes.string,
  }).isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.authState,
  customerReportsState: state.customerReportsState,
});

export default connect(mapStateToProps, {
  setBranchId,
  getCustomerReports,
  exportCustomerReports,
})(CustomerReports);
